import { createStore, applyMiddleware, compose } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { createBrowserHistory, createMemoryHistory } from 'history';
import { routerMiddleware } from 'connected-react-router';

import { isMobileApp, sendMobileTimeoutNotification } from '@ecs-uk/web-common-mobile-view-helpers';
import fetchMiddleware from './middleware/fetch';
import splitMiddleware from './middleware/split';
import timeoutMiddleware from './middleware/sessionTimeout';
import sessionMiddleware from './middleware/syncSession';
import rootReducer from './reducers/rootReducer';
import ecdMiddleware from './middleware/serializeEcd';
import { entitlementsApi } from './services/entitlementsApi';
import { getEnv } from './components/Context/env';
import isServer from './helpers/isServer';

export default (url = '/', csrf = '') => {
  // Create a history depending on the environment
  const env = getEnv();
  const history = isServer()
    ? createMemoryHistory({
        initialEntries: [url],
      })
    : createBrowserHistory();

  const enhancers = [];

  // Dev tools are helpful
  if (env.REACT_APP_ENV !== 'prod' && !isServer()) {
    const { __REDUX_DEVTOOLS_EXTENSION__: devToolsExtension } = window;

    if (typeof devToolsExtension === 'function') {
      enhancers.push(devToolsExtension());
    }
  }

  const middleware = [
    thunkMiddleware,
    entitlementsApi.middleware,
    fetchMiddleware,
    routerMiddleware(history),
    sessionMiddleware,
    ecdMiddleware(() => {
      if (isServer()) {
        return {};
      }

      return window;
    }),
    timeoutMiddleware(() => {
      if (isMobileApp()) {
        sendMobileTimeoutNotification();
        return;
      }

      window.location = `${env.REACT_APP_LOGOUT_URL}`;
    }),
    splitMiddleware(env),
  ];
  const composedEnhancers = compose(applyMiddleware(...middleware), ...enhancers);

  // Do we have preloaded state available? Great, save it.
  let initialState = !isServer() ? window.INITIAL_STATE || {} : {};
  const csrfToken = !isServer() ? window.CSRF_TOKEN || '' : csrf;

  initialState = {
    ...initialState,
    auth: {
      ...initialState.auth,
      csrfToken,
    },
  };

  if (!isServer() && (env.REACT_APP_ENV === 'local' || window.Cypress?.isIntegrationTest)) {
    initialState.session = JSON.parse(sessionStorage.getItem('sessionData')) || {};
  }

  // Delete it once we have it stored in a variable
  if (!isServer()) {
    delete window.INITIAL_STATE;
    delete window.CSRF_TOKEN;
  }

  // Create the store
  const store = createStore(rootReducer(history), initialState, composedEnhancers);

  return {
    store,
    history,
  };
};
